import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Banknote, CalendarDays, Hotel, MapPin, Users } from 'lucide-react';
import CrewBoardQuoteCarousel from '../components/crew/CrewBoardQuoteCarousel';
import CrewJobMaplet from '../components/crew/CrewJobMaplet';
import type { ReleasedCrewJobDetail, ReleasedCrewJobListItem } from '../types/crewJobs';
import { formatCrewJobHotelLine } from '../utils/crewJobsMap';
import { formatCrewJobPayBand, payBandFromJob } from '../utils/crewJobsPay';
import { getPublicSettings } from '../utils/appSettings';
import {
  CREW_BOARD_HERO_ROTATE_MS,
  CREW_BOARD_MEDIA,
  resolveCrewBoardHeroUrl,
  resolveCrewBoardHeroUrls,
} from '../utils/crewJobsBoardMedia';
import { ensureAuth } from '../utils/firebase';
import {
  executeCrewJobRecaptcha,
  fetchReleasedCrewJob,
  fetchReleasedCrewJobs,
  formatReleasedCrewJobDates,
  submitCrewJobCommit,
} from '../utils/crewJobsPublicApi';
import { captureCrewJobsAttribution, trackCrewJobsEvent } from '../utils/crewJobsAnalytics';
import './PublicCrewJobsPage.css';

const PublicCrewJobsPage: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const [listHeroes, setListHeroes] = useState<string[]>([CREW_BOARD_MEDIA.hero]);
  const [raiseHero, setRaiseHero] = useState<string>(CREW_BOARD_MEDIA.hero);

  useEffect(() => {
    captureCrewJobsAttribution();
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await ensureAuth();
        const settings = await getPublicSettings();
        if (cancelled) return;
        setListHeroes(resolveCrewBoardHeroUrls(settings.crewBoardListHeroes, settings.crewBoardListHero?.url));
        setRaiseHero(resolveCrewBoardHeroUrl(settings.crewBoardRaiseHero?.url));
      } catch {
        /* keep default crane shot */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (slug) return <CrewJobDetail slug={slug} heroSrc={raiseHero} />;
  return <CrewJobList key={listHeroes.join('|')} heroSrcs={listHeroes} />;
};

const splitCrewJobTitle = (title: string): { code: string | null; name: string } => {
  const match = title.trim().match(/^(\d{2,}\/)\s*(.+)$/);
  if (!match) return { code: null, name: title };
  return { code: match[1], name: match[2] };
};

const BoardPhoto: React.FC<{
  src: string;
  alt: string;
  className?: string;
  onError?: () => void;
}> = ({ src, alt, className, onError }) => {
  const [ok, setOk] = useState(true);

  useEffect(() => {
    setOk(true);
  }, [src]);

  if (!ok) return null;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => {
        setOk(false);
        onError?.();
      }}
    />
  );
};

const CrewBoardDock: React.FC = () => (
  <div className="crew-board-dock">
    <footer className="crew-board-foot">
      <p>
        Part-time raise gigs when a frame goes up and not to be considered full-time employment.
      </p>
      <p className="crew-board-foot-brand">Leiper's Creek Timber Frames</p>
    </footer>
  </div>
);

const BoardBrand: React.FC = () => {
  const [src, setSrc] = useState(CREW_BOARD_MEDIA.logo);
  return (
    <div className="crew-board-brand">
      <img
        src={src}
        alt="Leiper's Creek Timber Frames"
        className="crew-board-brand-mark"
        onError={() => {
          if (src !== CREW_BOARD_MEDIA.logoFallback) setSrc(CREW_BOARD_MEDIA.logoFallback);
        }}
      />
    </div>
  );
};

const BoardHeroMedia: React.FC<{ className?: string; photoSrcs: string[] }> = ({ className, photoSrcs }) => {
  const [videoOk, setVideoOk] = useState(true);
  const [failed, setFailed] = useState<string[]>([]);
  const [index, setIndex] = useState(0);

  const incomingKey = photoSrcs.join('|');
  const live = photoSrcs.filter(src => src && !failed.includes(src));
  const srcs = live.length ? live : [CREW_BOARD_MEDIA.hero];
  const srcKey = srcs.join('|');
  const useVideo = srcs.length === 1 && srcs[0] === CREW_BOARD_MEDIA.hero;

  useEffect(() => {
    setIndex(0);
    setFailed([]);
  }, [incomingKey]);

  useEffect(() => {
    if (srcs.length < 2) return undefined;
    if (typeof window === 'undefined') return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    const id = window.setInterval(() => {
      setIndex(current => (current + 1) % srcs.length);
    }, CREW_BOARD_HERO_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [srcKey, srcs.length]);

  const active = srcs[index % srcs.length] || srcs[0];

  return (
    <div className={`crew-board-hero-media ${className || ''}`} aria-hidden>
      {useVideo && videoOk && (
        <video
          className="crew-board-hero-video"
          src={CREW_BOARD_MEDIA.raisingVideo}
          muted
          loop
          playsInline
          autoPlay
          onError={() => setVideoOk(false)}
        />
      )}
      <div className="crew-board-hero-photos">
        {srcs.map((src, i) => (
          <BoardPhoto
            key={`${i}-${src}`}
            src={src}
            alt=""
            className={`crew-board-hero-photo${src === active ? ' is-active' : ''}`}
            onError={() => setFailed(current => (current.includes(src) ? current : [...current, src]))}
          />
        ))}
      </div>
    </div>
  );
};

const CrewJobList: React.FC<{ heroSrcs: string[] }> = ({ heroSrcs }) => {
  const [jobs, setJobs] = useState<ReleasedCrewJobListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackCrewJobsEvent({ event: 'page_view', path: '/crew-jobs' });
  }, []);

  useEffect(() => {
    void fetchReleasedCrewJobs()
      .then(setJobs)
      .catch(e => setError((e as Error).message || 'Could not load jobs'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="crew-board crew-board--list">
      <header className="crew-board-hero crew-board-hero--list">
        <BoardHeroMedia photoSrcs={heroSrcs} />
        <div className="crew-board-hero-veil" />
        <div className="crew-board-hero-wash" />
        <div className="crew-board-hero-inner">
          <BoardBrand />
        </div>
      </header>

      <section className="crew-board-quote-band">
        <h1 className="crew-board-quotes-live">Upcoming Jobs</h1>
        <CrewBoardQuoteCarousel />
      </section>

      <main id="upcoming-jobs" className="crew-board-main">
        <div className="crew-board-main-head">
          <h2>Upcoming Jobs</h2>
        </div>
        {loading && <p className="crew-board-muted">Loading jobs…</p>}
        {error && <p className="crew-board-error">{error}</p>}
        {!loading && !error && jobs.length === 0 && (
          <p className="crew-board-empty">
            Nothing posted right now. Check back when the next frame is ready to go up.
          </p>
        )}
        <div className="crew-board-grid">
          {jobs.map(job => {
            const payLabel = formatCrewJobPayBand(payBandFromJob(job));
            const cardTitle = (job.projectName || job.title || '').trim();
            return (
            <Link
              key={job.slug}
              className="crew-board-card"
              to={`/crew-jobs/${job.slug}`}
              onClick={() => {
                trackCrewJobsEvent({
                  event: 'job_card_click',
                  path: '/crew-jobs',
                  jobId: job.id || null,
                  jobSlug: job.slug,
                  jobTitle: job.title,
                });
              }}
            >
              <div className="crew-board-card-top">
                <h2 className="crew-board-project">{cardTitle}</h2>
                <span className={`crew-board-chip crew-board-chip--${job.status}`}>
                  {job.status === 'filled' ? 'Filled' : `${job.spotsLeft} spots`}
                </span>
              </div>
              <p className="crew-board-dates">
                <CalendarDays size={20} /> {formatReleasedCrewJobDates(job)}
              </p>
              {job.location && <p className="crew-board-meta"><MapPin size={14} /> {job.location}</p>}
              {payLabel && (
                <p className="crew-board-meta">
                  <Banknote size={14} /> {payLabel}
                </p>
              )}
              <p className="crew-board-meta">
                <Users size={14} /> {job.commitCount} of {job.crewNeeded} on the roster
              </p>
              <span className="crew-board-card-go">View job</span>
            </Link>
            );
          })}
        </div>
      </main>

      <CrewBoardDock />
    </div>
  );
};

const CrewJobDetail: React.FC<{ slug: string; heroSrc: string }> = ({ slug, heroSrc }) => {
  const [job, setJob] = useState<ReleasedCrewJobDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    trackCrewJobsEvent({ event: 'page_view', path: `/crew-jobs/${slug}` });
  }, [slug]);

  useEffect(() => {
    void fetchReleasedCrewJob(slug)
      .then(loaded => {
        setJob(loaded);
        trackCrewJobsEvent({
          event: 'job_view',
          path: `/crew-jobs/${slug}`,
          jobId: loaded.id || null,
          jobSlug: loaded.slug,
          jobTitle: loaded.title,
        });
      })
      .catch(e => setError((e as Error).message || 'Job not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!job) return;
    setSubmitting(true);
    setFormError(null);
    trackCrewJobsEvent({
      event: 'commit_cta_click',
      path: `/crew-jobs/${job.slug}`,
      jobId: job.id || null,
      jobSlug: job.slug,
      jobTitle: job.title,
    });
    try {
      const recaptchaToken = await executeCrewJobRecaptcha();
      const message = await submitCrewJobCommit({
        recaptchaToken,
        jobSlug: job.slug,
        firstName,
        lastName,
        email,
        phone,
        note,
      });
      trackCrewJobsEvent({
        event: 'commit_submit',
        path: `/crew-jobs/${job.slug}`,
        jobId: job.id || null,
        jobSlug: job.slug,
        jobTitle: job.title,
      });
      setSuccess(message);
    } catch (err) {
      setFormError((err as Error).message || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  const accepting = job?.status === 'released' && job.spotsLeft > 0;
  const hotelLine = job ? formatCrewJobHotelLine(job.hotelName, job.hotelLocation) : null;
  const showMaplet = Boolean(job?.location || hotelLine);
  const titleParts = job ? splitCrewJobTitle(job.title) : null;
  const payLabel = job ? formatCrewJobPayBand(payBandFromJob(job)) : null;

  return (
    <div className="crew-board">
      <header className="crew-board-hero crew-board-hero--detail">
        <BoardHeroMedia photoSrcs={[heroSrc]} />
        <div className="crew-board-hero-veil" />
        <div className="crew-board-hero-inner">
          <BoardBrand />
          {loading && <h1>Loading…</h1>}
          {error && <h1>Not found</h1>}
          {job && titleParts && (
            <>
              <h1 className="crew-board-job-title">
                {titleParts.code && <span className="crew-board-job-code">{titleParts.code}</span>}
                <span className="crew-board-job-name">{titleParts.name}</span>
              </h1>
              <p className="crew-board-dates">
                <CalendarDays size={28} /> {formatReleasedCrewJobDates(job)}
              </p>
              {hotelLine && (
                <p className="crew-board-meta">
                  <Hotel size={16} /> {hotelLine}
                </p>
              )}
              {payLabel && (
                <p className="crew-board-meta">
                  <Banknote size={16} /> {payLabel}
                </p>
              )}
              <p className="crew-board-meta">
                <Users size={16} /> {job.commitCount} of {job.crewNeeded} on the roster
              </p>
            </>
          )}
        </div>
      </header>
      <main className="crew-board-main crew-board-detail">
        <Link className="crew-board-back" to="/crew-jobs">
          <ArrowLeft size={20} /> All jobs
        </Link>
        {error && <p className="crew-board-error">{error}</p>}
        {job && (
          <>
            {showMaplet && <CrewJobMaplet job={job} />}
            <section className="crew-board-copy">
              <h2>Pay</h2>
              {payLabel && <p className="crew-board-pay-rate">{payLabel}</p>}
              <p className="crew-board-copy-job">
                {payLabel
                  ? 'This is the posted band for this raise, depending on experience and qualifications. We will confirm the rate, travel, and details with you before you are finalized on the roster.'
                  : 'Pay depends on experience and qualifications. We will confirm the rate, travel, and details with you before you are finalized on the roster.'}
              </p>
            </section>
            {job.description && (
              <section className="crew-board-copy">
                <h2>Notes</h2>
                <p className="crew-board-copy-job">{job.description}</p>
              </section>
            )}
            <section className="crew-board-form-card">
              {success ? (
                <div className="crew-board-success">
                  <h2>Thank you for committing</h2>
                  <p>{success}</p>
                </div>
              ) : !accepting ? (
                <p className="crew-board-muted">This raise is full or no longer accepting commitments.</p>
              ) : (
                <form onSubmit={e => void handleSubmit(e)}>
                  <h2>Commit to this raise</h2>
                  <p className="crew-board-muted">
                    Name, email, and phone. Signing up does not put you on the roster.
                    Someone from our team will call or email to confirm pay, travel, and details first.
                  </p>
                  <div className="crew-board-form-row">
                    <label>
                      First name
                      <input required value={firstName} onChange={e => setFirstName(e.target.value)} />
                    </label>
                    <label>
                      Last name
                      <input required value={lastName} onChange={e => setLastName(e.target.value)} />
                    </label>
                  </div>
                  <label>
                    Email
                    <input required type="email" value={email} onChange={e => setEmail(e.target.value)} />
                  </label>
                  <label>
                    Phone
                    <input required type="tel" value={phone} onChange={e => setPhone(e.target.value)} />
                  </label>
                  <label>
                    Note <span>(optional)</span>
                    <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} />
                  </label>
                  {formError && <p className="crew-board-error">{formError}</p>}
                  <button type="submit" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Commit to this job'}
                  </button>
                </form>
              )}
            </section>
          </>
        )}
      </main>
      <CrewBoardDock />
    </div>
  );
};

export default PublicCrewJobsPage;
